import { BikeOnMap } from './mapTypes';
import { GOOGLE_MAPS_API_KEY } from '@/hooks/googleMap/config';
import { addBikesMarkers, centerMapOnBike } from '@/hooks/googleMap/markerManager';
import { toast } from '@/components/ui/sonner';

// Отслеживание статуса загрузки API
let apiLoadingInProgress = false;
let apiLoaded = false;
let apiLoadedCallback: (() => void)[] = [];
let initAttempts = 0;
const MAX_INIT_ATTEMPTS = 3;

/**
 * Инициализация карты Google для компонента курьеров
 */
export const initializeMapForCouriers = (mapRef: HTMLDivElement, bikesData: BikeOnMap[], onMapReady: (map: any, googleMaps: any) => void) => {
  // Проверяем наличие API Google Maps
  if (window.google && window.google.maps) {
    console.log('Google Maps API уже загружен, инициализируем карту');
    initializeMapWithRetry(mapRef, bikesData, onMapReady);
    return;
  }
  
  console.log('Google Maps не загружены, загружаем API с ключом');
  loadGoogleMapsForMap(() => {
    console.log('API Google Maps успешно загружен, инициализируем карту');
    initializeMapWithRetry(mapRef, bikesData, onMapReady);
  }, (error) => {
    toast.error(`Ошибка загрузки API Google Maps: ${error}`);
  });
};

/**
 * Инициализация карты с повторными попытками при ошибке
 */
const initializeMapWithRetry = (mapRef: HTMLDivElement, bikesData: BikeOnMap[], onMapReady: (map: any, googleMaps: any) => void) => {
  console.log(`Инициализация карты Google в контейнере, попытка ${initAttempts + 1}/${MAX_INIT_ATTEMPTS}`);
  
  // Проверяем наличие google и Map
  if (!window.google || !window.google.maps || typeof window.google.maps.Map !== 'function') {
    console.warn('Объект google.maps.Map не доступен, пробуем перезагрузить API');
    
    if (initAttempts < MAX_INIT_ATTEMPTS) {
      initAttempts++;
      
      setTimeout(() => {
        loadGoogleMapsForMap(() => {
          console.log(`API Google Maps перезагружен, повторная попытка ${initAttempts}`);
          initializeMapWithRetry(mapRef, bikesData, onMapReady);
        }, (error) => {
          toast.error(`Ошибка загрузки API Google Maps: ${error}`);
        });
      }, 1000 * initAttempts); // Увеличиваем задержку с каждой попыткой
      
      return;
    } else {
      toast.error('Не удалось инициализировать Google Maps после нескольких попыток');
      return;
    }
  }
  
  // Сбрасываем счетчик попыток при успешной инициализации
  initAttempts = 0;
  
  try {
    console.log('Создание экземпляра карты');
    
    // Сначала проверяем, есть ли уже существующий экземпляр карты
    if (mapRef.__gMap) {
      console.log('Найден существующий экземпляр карты, переиспользуем его');
      onMapReady(mapRef.__gMap, window.google.maps);
      return;
    }
    
    // Создаем карту
    const map = new window.google.maps.Map(mapRef, {
      center: { lat: 55.751244, lng: 37.618423 }, // Москва
      zoom: 11,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
    });
    
    // Сохраняем ссылку на карту
    mapRef.__gMap = map;
    
    // Вызываем callback
    onMapReady(map, window.google.maps);
    
  } catch (error) {
    console.error('Ошибка при инициализации карты:', error);
    toast.error('Произошла ошибка при инициализации карты');
    
    // При ошибке инициализации пробуем очистить и создать заново
    if (mapRef.__gMap) {
      try {
        console.log('Очистка неудачно инициализированной карты');
        delete mapRef.__gMap;
      } catch (e) {
        console.error('Ошибка при очистке карты:', e);
      }
    }
  }
};

/**
 * Загрузка скрипта Google Maps с защитой от дублирования
 */
export const loadGoogleMapsForMap = (onLoad: () => void, onError?: (error: string) => void) => {
  // Если скрипт уже загружен, просто вызываем колбэк
  if (window.google && window.google.maps && typeof window.google.maps.Map === 'function') {
    console.log('Google Maps уже загружены, инициализация карты');
    onLoad();
    return;
  }
  
  // Если API уже загружен, но ещё не готов к использованию
  if (apiLoaded) {
    console.log('Google Maps API загружен, но ещё не готов');
    const checkInterval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.Map) {
        clearInterval(checkInterval);
        onLoad();
      }
    }, 100);
    return;
  }
  
  // Если загрузка уже начата, добавляем колбэк в очередь
  if (apiLoadingInProgress) {
    console.log('Загрузка API Google Maps уже выполняется, добавляем колбэк в очередь');
    apiLoadedCallback.push(onLoad);
    return;
  }
  
  console.log('Инициируем загрузку API Google Maps с ключом');
  apiLoadingInProgress = true;
  apiLoadedCallback.push(onLoad);
  
  // Проверяем, есть ли уже скрипт для загрузки Google Maps API
  const existingScript = document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`);
  if (existingScript) {
    console.log('Найден существующий скрипт Google Maps API, используем его');
    
    // Создаем функцию проверки загрузки API
    const checkIfLoaded = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.Map) {
        clearInterval(checkIfLoaded);
        console.log('Google Maps API загружен с существующего скрипта');
        apiLoaded = true;
        apiLoadingInProgress = false;
        
        // Вызываем все накопленные колбэки
        apiLoadedCallback.forEach(callback => callback());
        apiLoadedCallback = [];
      }
    }, 100);
    
    // Устанавливаем таймаут на случай, если API не загрузится
    setTimeout(() => {
      clearInterval(checkIfLoaded);
      if (!apiLoaded) {
        console.error('Таймаут при ожидании загрузки Google Maps API');
        apiLoadingInProgress = false;
        if (onError) onError('Таймаут при загрузке Google Maps API');
      }
    }, 10000);
    
    return;
  }
  
  // Создаем скрипт для загрузки API без использования колбэка в URL
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
  script.async = true;
  script.defer = true;
  
  script.onload = () => {
    console.log('API Google Maps загружен успешно');
    
    // Проверяем загрузку API с интервалом
    const checkApiLoaded = setInterval(() => {
      if (window.google && window.google.maps && typeof window.google.maps.Map === 'function') {
        clearInterval(checkApiLoaded);
        apiLoaded = true;
        apiLoadingInProgress = false;
        
        console.log('API Google Maps готов к использованию, вызываем колбэки');
        
        // Вызываем все накопленные колбэки
        apiLoadedCallback.forEach(callback => callback());
        apiLoadedCallback = []; // Очищаем массив колбэков
      }
    }, 100);
    
    // Таймаут для ожидания полной загрузки API
    setTimeout(() => {
      clearInterval(checkApiLoaded);
      if (!apiLoaded) {
        console.error('Таймаут при ожидании инициализации Google Maps API');
        apiLoadingInProgress = false;
        if (onError) onError('Таймаут при инициализации Google Maps API');
        apiLoadedCallback = [];
      }
    }, 10000);
  };
  
  script.onerror = (event) => {
    console.error('Ошибка загрузки API Google Maps:', event);
    apiLoadingInProgress = false;
    
    // Очищаем колбэки в случае ошибки
    apiLoadedCallback = [];
    
    if (onError) onError('Не удалось загрузить скрипт Google Maps API');
  };
  
  document.head.appendChild(script);
};

// Реэкспортируем функции для работы с маркерами
export { addBikesMarkers, centerMapOnBike };

// Добавляем типы только для конкретного контейнера карты
declare global {
  interface HTMLDivElement {
    __gMap?: any;
  }
  
  // Удаляем дублирующее объявление Window, так как оно уже есть в global.d.ts
}
